import { AlertTriangle, FlaskConical, Microscope } from "lucide-react";

import type { InteractionCandidate } from "@shared/schema";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Tooltip } from "./ui/tooltip";
import { EVIDENCE_TIER_META } from "@/lib/evidence";
import { cn } from "@/lib/utils";

interface Props {
  interactions: InteractionCandidate[];
  onSelect: (row: InteractionCandidate) => void;
}

const FACTORS = [
  { key: "experimental", label: "Experimental" },
  { key: "aiConfidence", label: "AI / comp" },
  { key: "diseaseRelevance", label: "Disease" },
  { key: "novelty", label: "Novelty" },
  { key: "tractability", label: "Tractability" },
  { key: "conservation", label: "Conservation" },
] as const;

export function LabPriorityPanel({ interactions, onSelect }: Props) {
  const top = interactions.slice(0, 10);
  if (top.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        <p className="font-medium text-foreground">Nothing ranked yet.</p>
        <p className="mt-1">Run a search to populate the Lab Priority Panel.</p>
      </div>
    );
  }
  return (
    <section className="space-y-3">
      <header className="flex items-baseline justify-between gap-2">
        <div>
          <h2 className="font-display text-lg font-semibold">What to validate first</h2>
          <p className="text-xs text-muted-foreground">
            Top {top.length} ranked candidates &mdash; factor breakdown (UI-03), plain-language rationale,
            and explicit caveats.
          </p>
        </div>
      </header>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-2">
        {top.map((row, idx) => (
          <PriorityCard key={row.id} row={row} rank={idx + 1} onSelect={onSelect} />
        ))}
      </div>
    </section>
  );
}

function PriorityCard({
  row,
  rank,
  onSelect,
}: {
  row: InteractionCandidate;
  rank: number;
  onSelect: (row: InteractionCandidate) => void;
}) {
  const lp = row.labPriority;
  const tier = EVIDENCE_TIER_META[row.evidenceTier];
  const priorityBand =
    (lp?.score ?? 0) >= 0.6 ? "high" : (lp?.score ?? 0) >= 0.4 ? "med" : "low";
  return (
    <Card className="overflow-hidden transition-shadow hover:shadow-md">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "grid h-7 w-7 place-items-center rounded-md text-xs font-semibold text-white",
                priorityBand === "high" && "bg-accent",
                priorityBand === "med" && "bg-secondary",
                priorityBand === "low" && "bg-muted text-muted-foreground",
              )}
            >
              #{rank}
            </span>
            <CardTitle className="font-mono">
              {row.sourceGene} <span className="text-muted-foreground">×</span> {row.partnerGene}
            </CardTitle>
          </div>
          <CardDescription className="mt-1 flex flex-wrap items-center gap-1.5">
            <Badge color={tier.color}>{tier.label}</Badge>
            {lp?.score != null && (
              <span className="text-foreground">
                Priority{" "}
                <span className="font-mono font-semibold">
                  {Math.round((lp.score ?? 0) * 100)}
                </span>
                /100
              </span>
            )}
          </CardDescription>
        </div>
        <button
          type="button"
          onClick={() => onSelect(row)}
          className="text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          Details →
        </button>
      </CardHeader>
      <CardContent className="space-y-3">
        <FactorBars lp={lp} />
        {lp?.rationale && lp.rationale.length > 0 && (
          <div className="text-xs">
            <div className="mb-1 font-semibold uppercase tracking-wide text-muted-foreground">
              Why prioritized
            </div>
            <ul className="space-y-0.5">
              {lp.rationale.slice(0, 3).map((r, i) => (
                <li key={i} className="text-foreground">&middot; {r}</li>
              ))}
            </ul>
          </div>
        )}
        {lp?.caveats && lp.caveats.length > 0 && (
          <div className="rounded-md border border-[hsl(38_90%_60%/_0.4)] bg-[hsl(44_90%_92%/_0.8)] p-2 text-xs dark:bg-[hsl(38_50%_20%/_0.6)]">
            <div className="mb-1 flex items-center gap-1 font-semibold text-[hsl(28_80%_32%)] dark:text-[hsl(44_90%_82%)]">
              <AlertTriangle className="h-3 w-3" /> Caution
            </div>
            <ul className="space-y-0.5">
              {lp.caveats.map((c, i) => (
                <li key={i}>&middot; {c}</li>
              ))}
            </ul>
          </div>
        )}
        {(lp?.suggestedPrimaryAssay || lp?.suggestedOrthogonalAssay) && (
          <div className="flex flex-wrap items-center gap-3 text-xs">
            {lp?.suggestedPrimaryAssay && (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <FlaskConical className="h-3 w-3" />
                <span className="text-foreground">{lp.suggestedPrimaryAssay}</span>
              </span>
            )}
            {lp?.suggestedOrthogonalAssay && (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Microscope className="h-3 w-3" />
                <span className="text-foreground">{lp.suggestedOrthogonalAssay}</span>
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FactorBars({ lp }: { lp: InteractionCandidate["labPriority"] }) {
  if (!lp) return null;
  return (
    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
      {FACTORS.map((f) => {
        const value = lp.factors[f.key];
        return (
          <div key={f.key} className="flex items-center gap-2">
            <dt className="w-20 shrink-0 text-muted-foreground">
              <Tooltip content={`${f.label} (0–100): factor contribution to lab priority`}>
                <span>{f.label}</span>
              </Tooltip>
            </dt>
            <dd className="flex flex-1 items-center gap-1.5">
              <span className="score-bar">
                <span className="score-bar__fill" style={{ width: `${value * 100}%` }} />
              </span>
              <span className="w-6 text-right font-mono tabular-nums text-muted-foreground">
                {Math.round(value * 100)}
              </span>
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
